import { motion } from "framer-motion";
import { Check, TrendingUp, PiggyBank } from "lucide-react";
import { waLink } from "../../data/site";
import { Reveal } from "./Reveal";

const POINTS = [
  "Atendimento e benefícios pensados para essa fase da vida",
  "Condições de contratação conforme região e perfil do beneficiário",
  "Orientação completa, com calma e clareza, do início ao fim",
];

export const MedSenior = () => (
  <section id="senior" data-testid="medsenior-section" className="bg-paper px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
    <Reveal className="relative mx-auto max-w-7xl">
      <motion.img
        src="/assets/folha-medsenior.png"
        alt=""
        aria-hidden="true"
        data-testid="medsenior-leaf"
        animate={{ rotate: [0, 8, 0, -5, 0], y: [0, -6, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-10 -left-4 z-20 w-24 drop-shadow-[0_10px_24px_rgba(0,0,0,0.45)] sm:-left-10 sm:w-28"
      />
      <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-[#6DBE45]/30 bg-gradient-to-br from-[#22321C] via-[#1E2D19] to-[#18240F] p-6 shadow-[0_40px_100px_-40px_rgba(109,190,69,0.25)] sm:p-12 lg:p-16">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#6DBE45]/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[#6DBE45]/10 blur-3xl" />

        <div className="relative grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-[#8FD66B]">
                MedSênior
              </p>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#6DBE45]/40 bg-[#6DBE45]/10 px-3.5 py-1.5 text-[11px] font-bold text-[#8FD66B]">
                <TrendingUp className="h-3.5 w-3.5" />
                Alto volume de contratação
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#6DBE45]/40 bg-[#6DBE45]/15 px-3.5 py-1.5 text-[11px] font-bold text-[#A5E383]">
                <PiggyBank className="h-3.5 w-3.5" />
                Excelente custo-benefício
              </span>
            </div>

            <h2 className="mt-6 max-w-xl font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Uma opção pensada para a{" "}
              <em className="italic text-[#8FD66B]">3ª idade</em>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg">
              O MedSênior é uma opção de plano de saúde voltada especialmente para a
              terceira idade, com atendimento e benefícios pensados para essa fase da
              vida e ótimas condições de contratação, conforme região e perfil do
              beneficiário.
            </p>

            <ul className="mt-8 space-y-4">
              {POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#6DBE45]/20">
                    <Check className="h-3 w-3 text-[#8FD66B]" strokeWidth={3} />
                  </span>
                  <span className="text-sm leading-relaxed text-stone-300">{p}</span>
                </li>
              ))}
            </ul>

            <a
              href={waLink("Olá! Gostaria de conhecer o MedSênior para a terceira idade.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="medsenior-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#6DBE45] px-8 py-4 text-sm font-bold text-white shadow-[0_12px_40px_-10px_rgba(109,190,69,0.5)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#5CAE39]"
            >
              Conhecer o MedSênior
            </a>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <div className="overflow-hidden rounded-[2rem] border border-[#6DBE45]/25 bg-[#22321C] shadow-2xl shadow-black/40">
                <img
                  src="/assets/ilustra-casal.png"
                  alt="Ilustração de um casal da terceira idade de mãos dadas"
                  data-testid="medsenior-illustration"
                  className="aspect-[16/10] w-full object-cover"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="absolute -bottom-5 -left-4 rounded-2xl border border-white/10 bg-surface/95 px-5 py-3.5 shadow-xl backdrop-blur-xl sm:-left-8"
              >
                <p className="font-display text-sm font-semibold text-ink">
                  Custo-benefício que cabe no bolso
                </p>
                <p className="mt-0.5 text-xs text-stone-500">
                  Condições conforme região e perfil
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);
